import React from 'react';

/**
 * Shared "scoring in progress" surfaces.
 *
 * Every variant is portrait-native: no fixed widths, wrapping rows, and text that
 * truncates rather than pushing the layout wider than a 360px viewport.
 */

function Spinner({ className = 'w-3 h-3' }) {
  return (
    <span
      className={`${className} inline-block rounded-full border-2 border-[#00D4FF]/30 border-t-[#00D4FF] animate-spin shrink-0`}
      aria-hidden="true"
    />
  );
}

/** Inline chip for a single row in a list (buyer row, property row). */
export function ScoringChip({ state, failedJob, onRetry, className = '' }) {
  if (!state) return null;

  if (state === 'failed') {
    return (
      <span
        className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded text-[11px] font-medium
          text-red-300 bg-red-500/10 border border-red-500/30 max-w-full ${className}`}
        title={failedJob?.error || 'Scoring failed'}
      >
        <span className="shrink-0">Scoring failed</span>
        {onRetry && failedJob && (
          <button
            type="button"
            onClick={(e) => { e.preventDefault(); e.stopPropagation(); onRetry(failedJob.job_id); }}
            className="shrink-0 underline underline-offset-2 hover:text-red-200 focus:outline-none"
          >
            Retry
          </button>
        )}
      </span>
    );
  }

  return (
    <span
      className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded text-[11px] font-medium
        text-[#00D4FF] bg-[#00D4FF]/10 border border-[#00D4FF]/30 max-w-full ${className}`}
      title="Scoring runs in the background — this clears on its own"
    >
      <Spinner />
      <span className="shrink-0">Scoring…</span>
    </span>
  );
}

/**
 * Page-level banner. Shows how many items are scoring, and lists failures with a retry.
 * Renders nothing when there is no pending or failed work, so it self-clears.
 */
export function ScoringBanner({ status, onRetry, label = 'items', className = '' }) {
  const scoring = status?.scoring_count || 0;
  const failed = status?.failed || [];
  if (!scoring && !failed.length) return null;

  return (
    <div className={`mb-4 space-y-2 ${className}`}>
      {scoring > 0 && (
        <div className="flex items-start gap-2 px-3 py-2 rounded-lg bg-[#00D4FF]/10 border border-[#00D4FF]/30">
          <Spinner className="w-3.5 h-3.5 mt-0.5" />
          <div className="min-w-0 text-xs text-[#B0BEC5]">
            <span className="text-[#00D4FF] font-medium">
              Scoring {scoring} {scoring === 1 ? label.replace(/s$/, '') : label}…
            </span>{' '}
            This runs in the background — scores appear here automatically.
          </div>
        </div>
      )}

      {failed.map((f) => (
        <div
          key={f.job_id}
          className="flex flex-wrap items-center gap-x-2 gap-y-1 px-3 py-2 rounded-lg bg-red-500/10 border border-red-500/30"
        >
          <span className="text-xs text-red-300 font-medium shrink-0">Scoring failed</span>
          <span className="text-xs text-[#B0BEC5] min-w-0 truncate flex-1">{f.name}</span>
          {onRetry && (
            <button
              type="button"
              onClick={() => onRetry(f.job_id)}
              className="shrink-0 px-2 py-0.5 rounded text-[11px] font-medium text-red-200
                border border-red-400/40 hover:bg-red-500/20 transition-colors"
            >
              Retry
            </button>
          )}
        </div>
      ))}
    </div>
  );
}

export default ScoringChip;
